import { useCallback } from 'react';
import type { ParamListBase } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { useSafeFocusEffect, useSafePathname, useSafeRouter } from '@sg/garnish';

import { useIsExpoRouterEnabled } from '@order/shared/hooks';

import { redirectsMapping } from '../../../../navigation/AppNavigation.redirects';

// ─────────────────────────────────────────────────────────────────────────────

// Here we handle 301 redirects using the redirects mapping.
export const usePermanentRedirects = (params: PermanentRedirectsParams) => {
  const { navigation, route } = params;

  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const router = useSafeRouter();
  const pathname = useSafePathname();

  const path = route?.path ?? pathname;
  const redirectTo = path ? redirectsMapping[path] : null;
  const expoRedirectTo = path ? EXPO_ROUTER_REDIRECTS[path] : null;

  useSafeFocusEffect(
    useCallback(() => {
      if (isExpoRouterEnabled && expoRedirectTo) {
        router?.replace(expoRedirectTo);

        return;
      }

      if (redirectTo && navigation) {
        navigation.replace(redirectTo.screen, redirectTo.params);
      }
    }, [isExpoRouterEnabled, expoRedirectTo, router, redirectTo, navigation]),
  );

  return Boolean(isExpoRouterEnabled ? expoRedirectTo : redirectTo);
};

// ─── Constants ───────────────────────────────────────────────────────────────

const EXPO_ROUTER_REDIRECTS: Record<string, string> = {
  '/location': '/menu',
  '/menu': '/account',
  '/profile': '/account/profile',
  '/account/profile': '/account/profile',
  '/challenges': '/sg-rewards',
  '/payment': '/account/payment-and-gift-cards',
  '/addresses': '/account/addresses',
  '/orders': '/account/orders',
  '/favorites': '/account/favorites',
  '/account-dietary-restrictions': '/account/dietary-restrictions',
};

// ─── Types ───────────────────────────────────────────────────────────────────

type PermanentRedirectsParams = {
  navigation: NativeStackNavigationProp<ParamListBase> | null;
  route: { path?: string } | null;
};
